import { Box, Container, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { FieldType } from '.';
import {
  MeasurementCardResponse,
  SectionResponse,
} from '../../.generated/admin-api';
import { FieldValueState } from './';
import { RegistrationFlowField } from './RegistrationFlowField';
import { validateField } from './fieldValidation';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';

type RegistrationFlowSectionInputProps = {
  measurementCard: MeasurementCardResponse;
  section: SectionResponse;
  fieldValues: FieldValueState[];
  disabled?: boolean;
  backButton?: React.ReactNode;
  nextButton?: React.ReactNode;
  onFieldValueChangeOrValidated?: (
    fieldId: string,
    value: unknown,
    isValid: boolean,
  ) => void;
  onAutofillChanged?: (autoFillFields: FieldValueState[]) => void;
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  buttons: {
    marginTop: theme.spacing(3),
  },
}));

const RegistrationFlowSection: React.FC<RegistrationFlowSectionInputProps> = (
  props,
) => {
  const {
    measurementCard,
    section,
    fieldValues,
    backButton,
    nextButton,
    onFieldValueChangeOrValidated,
    onAutofillChanged,
    disabled,
  } = props;

  const classes = useStyles();

  const basicDataFieldValues = useSelector((state: ApplicationState) => {
    const departmentId = state.user.selectedDepartment?.id;
    if (!departmentId) {
      return undefined;
    }
    return state.stacks.departments[departmentId]?.basicDataFieldValues;
  });
  const [fields, setFields] = useState<FieldType[]>([]);

  useEffect(() => {
    const fields = section.fields
      .map((fieldId) => {
        const field = measurementCard.fields.find(
          (field) => field.id === fieldId,
        );
        if (field === undefined) {
          // TODO: DHM: How to handle this?
          throw new TypeError('Unable to find field in measurement card');
        }
        return field;
      })
      .filter((field) => !field.readOnly);

    if (onAutofillChanged) {
      if (basicDataFieldValues) {
        onAutofillChanged(
          fields
            .filter((field) => !!basicDataFieldValues[field.id])
            .map((field) => {
              // validate autofill values for this section.
              const value = basicDataFieldValues[field.id];
              const validationResult = validateField(field, value);
              return {
                fieldId: field.id,
                value: value,
                isValid: validationResult.isValid,
              };
            }),
        );
      } else {
        onAutofillChanged([]);
      }
    }

    setFields(fields);
  }, [section]);

  return (
    <>
      <Container className={classes.container}>
        {fields?.map((field) => {
          const fieldValue = fieldValues.find(
            (fieldValue) => fieldValue.fieldId === field.id,
          );
          const defaultValue =
            fieldValue !== undefined ? fieldValue.value : undefined;
          return (
            <RegistrationFlowField
              key={field.id}
              field={field}
              defaultValue={defaultValue}
              onValueChangeOrValidated={onFieldValueChangeOrValidated}
              disabled={disabled}
            />
          );
        })}
        <Box className={classes.buttons}>
          {backButton} {nextButton}
        </Box>
      </Container>
    </>
  );
};

export { RegistrationFlowSection };
